import { useState, useMemo, useEffect } from 'react';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { usePortfolioPnL } from '../../hooks/useCalculations';
import { MAX_PACKAGES } from '../../data/conjointData';
import { PackageCard } from './PackageCard';
import { Plus } from 'lucide-react';

interface MobilePackageSelectorProps {
  demandMode: 'ceiling' | 'split';
}

export function MobilePackageSelector({ demandMode }: MobilePackageSelectorProps) {
  const [selected, setSelected] = useState(0);
  const packages = useSimulatorStore(s => s.packages);
  const addPackage = useSimulatorStore(s => s.addPackage);
  const portfolio = usePortfolioPnL();

  useEffect(() => {
    if (selected >= packages.length) setSelected(Math.max(0, packages.length - 1));
  }, [packages.length, selected]);

  const bestIdx = useMemo(() => {
    if (portfolio.packages.length === 0) return -1;
    let best = 0;
    const contributions = portfolio.packages;
    for (let i = 1; i < contributions.length; i++) {
      const val = demandMode === 'ceiling' ? contributions[i].contributionMarginCeiling : contributions[i].contributionMarginSplit;
      const bestVal = demandMode === 'ceiling' ? contributions[best].contributionMarginCeiling : contributions[best].contributionMarginSplit;
      if (val > bestVal) best = i;
    }
    const bestMargin = demandMode === 'ceiling' ? contributions[best].contributionMarginCeiling : contributions[best].contributionMarginSplit;
    return bestMargin > 0 ? best : -1;
  }, [portfolio, demandMode]);

  return (
    <div>
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {packages.map((pkg, i) => (
          <button
            key={i}
            onClick={() => setSelected(i)}
            className={`flex-1 min-w-0 py-2.5 text-sm font-medium rounded-lg transition-all border ${
              selected === i
                ? 'bg-chart-teal/15 text-chart-teal border-chart-teal/30'
                : 'text-secondary border-border hover:text-primary hover:bg-surface'
            }`}
          >
            {pkg.name}
          </button>
        ))}
        {packages.length < MAX_PACKAGES && (
          <button
            onClick={addPackage}
            className="shrink-0 w-10 py-2.5 rounded-lg border border-dashed border-border/60 hover:border-chart-teal/40 text-muted hover:text-chart-teal transition-colors flex items-center justify-center"
          >
            <Plus size={16} />
          </button>
        )}
      </div>
      {packages[selected] && portfolio.packages[selected] && (
        <PackageCard
          key={selected}
          index={selected}
          config={packages[selected]}
          contribution={portfolio.packages[selected]}
          demandMode={demandMode}
          isRecommended={selected === bestIdx}
        />
      )}
    </div>
  );
}
